// Competitive Intel V2 API client — `/api/v2/competitive-intel` (backend handoff
// FE_HANDOFF_V2_ENDPOINTS.md). Used by the Audit wizard (reportCompetitiveIntel.slice.js) and the
// report view's Competitive layer (competitiveIntelV2.slice.js).
//
// Same thin promise wrappers as ../intelligenceReportApi.js, with two differences:
// - The base is /api/v2, so every call passes `host` (API_HOST ends in /api/v1).
// - Each call resolves with the envelope's `data` on any 2xx, and rejects with a CiApiError carrying
//   the HTTP status, the backend's `message` (written for the member: show it as it is), the 422
//   field `errors` and the 429 `retryAfter`.
//
// Ids are integers; documents are addressed by path (e.g. "summary/one-month").

import arClient from '../../../util/api_client';
import { API_HOST } from '../../../constants/apiUrls';

export const CI_V2_HOST = `${API_HOST.replace(/\/api\/v1\/?$/, '')}/api/v2/competitive-intel`;

export class CiApiError extends Error {
  constructor({ status, message, errors, retryAfter, cancelled, feature }) {
    super(message || 'Something went wrong. Please try again.');
    this.name = 'CiApiError';
    this.status = status ?? null;
    this.errors = errors || null;
    this.retryAfter = retryAfter ?? null;
    this.cancelled = Boolean(cancelled);
    // 403 "Usage limit reached for Competitive Intel…": the plan feature that ran out.
    this.feature = feature || null;
  }
}

const httpStatusOf = (resp) => {
  if (resp?.httpStatus != null) return resp.httpStatus;
  return typeof resp?.status === 'number' ? resp.status : null;
};

const settle = (resp, resolve, reject) => {
  const status = httpStatusOf(resp);
  const failed =
    !resp || resp.isCancelled || resp.status === 'ERROR' || (status != null && status >= 300);
  if (failed) {
    reject(
      new CiApiError({
        status,
        message: resp?.message && resp.message !== 'ok' ? resp.message : resp?.error,
        errors: resp?.errors,
        retryAfter: resp?.retry_after,
        cancelled: resp?.isCancelled,
        feature: resp?.feature,
      })
    );
    return;
  }
  resolve({ data: resp.data, meta: resp.meta || null, status });
};

const v2Get = (endpoint) =>
  new Promise((resolve, reject) =>
    arClient.doApiCallGet(endpoint, (resp) => settle(resp, resolve, reject), { host: CI_V2_HOST })
  );

// Mutations never auto-retry: a retried start or cancel would hit the backend twice.
const v2Send = (method, endpoint, body = {}, options = {}) =>
  new Promise((resolve, reject) =>
    arClient.doApiCallV2(endpoint, body, (resp) => settle(resp, resolve, reject), {
      host: CI_V2_HOST,
      method,
      noRetry: true,
      ...options,
    })
  );

const id = (value) => encodeURIComponent(value);
const reportPath = (reportId) => `/reports/${id(reportId)}`;

// ── Reports ──

/** GET reports — the report list. `query`: { 'filter[status]', sort, page, perPage }. */
export const listReports = (query = {}) => {
  const params = new URLSearchParams(
    Object.entries(query).filter(([, value]) => value != null && value !== '')
  ).toString();
  return v2Get(`/reports${params ? `?${params}` : ''}`);
};

/** POST reports — a new report; the product check starts by itself. → report (status pending). */
export const createReport = ({ productName, productUrl }) =>
  v2Send('POST', '/reports', { productName, productUrl });

/** GET reports/{report} — the list fields + state, message, shown, setup, runInProgress, lastAttempt. */
export const getReport = (reportId) => v2Get(reportPath(reportId));

/**
 * PATCH reports/{report} — any of productName, productUrl (a new product: checked again),
 * notificationEmails, status: 'archived'.
 */
export const updateReport = (reportId, changes) => v2Send('PATCH', reportPath(reportId), changes);

// ── Product and product type ──

/** GET reports/{report}/validation — the product as checked (404 until it has been accepted). */
export const getValidation = (reportId) => v2Get(`${reportPath(reportId)}/validation`);

/** PATCH reports/{report}/validation — confirm the type; competitor discovery then starts. */
export const confirmProductType = (reportId, productType) =>
  v2Send('PATCH', `${reportPath(reportId)}/validation`, { productType });

/** POST reports/{report}/product-types — a typed type → 202 with a `product_type_check` setup step. */
export const addProductType = (reportId, productType) =>
  v2Send('POST', `${reportPath(reportId)}/product-types`, { productType });

// ── Competitors ──

/** GET reports/{report}/competitors — every competitor that isn't archived, in the order added. */
export const listCompetitors = (reportId) => v2Get(`${reportPath(reportId)}/competitors`);

/** POST reports/{report}/competitors — a typed competitor → 202 with a `competitor_check` step. */
export const addCompetitor = (reportId, competitorName) =>
  v2Send('POST', `${reportPath(reportId)}/competitors`, { competitorName });

/** PATCH reports/{report}/competitors/{competitor} — keep ('active') or reject ('rejected'). */
export const setCompetitorStatus = (reportId, competitorId, status) =>
  v2Send('PATCH', `${reportPath(reportId)}/competitors/${id(competitorId)}`, { status });

// ── Setup steps ──

/** GET reports/{report}/setup-steps/{setupStep} — status, input, acceptedValue, message. */
export const getSetupStep = (reportId, setupStepId) =>
  v2Get(`${reportPath(reportId)}/setup-steps/${id(setupStepId)}`);

// ── Runs ──

/**
 * POST reports/{report}/runs — start the report (nothing runs before this). → the run.
 * `includeHistoricalData` also builds the 3-month, 6-month and all-time summaries (false if left out).
 */
export const startRun = (reportId, { includeHistoricalData = false } = {}) =>
  v2Send('POST', `${reportPath(reportId)}/runs`, { includeHistoricalData });

/** GET reports/{report}/runs/{run}/events — the live feed, oldest first. */
export const getRunEvents = (reportId, runId) =>
  v2Get(`${reportPath(reportId)}/runs/${id(runId)}/events`);

/** POST reports/{report}/cancellations — stop whatever is working on the report. */
export const cancelReport = (reportId, { keepalive = false } = {}) =>
  v2Send('POST', `${reportPath(reportId)}/cancellations`, {}, keepalive ? { keepalive } : {});

/** True for the 403 a start or rerun answers when the plan's Competitive Intel analyses are used up. */
export const isUsageLimitError = (error) =>
  error?.status === 403 &&
  (error.feature === 'competitive_intel' || /usage limit/i.test(error.message || ''));

/**
 * POST reports/{report}/reruns — run a failed or cancelled report again. It goes on from where the
 * analysis stopped (`runInProgress.resumed` turns true), starting afresh only when there's nothing
 * to resume.
 */
export const rerunReport = (reportId) => v2Send('POST', `${reportPath(reportId)}/reruns`);

/** POST reports/{report}/updates — bring a finished report up to date (can be refused later). */
export const updateFinishedReport = (reportId) => v2Send('POST', `${reportPath(reportId)}/updates`);

// ── Content ──

/**
 * GET reports/{report}/document-sets/{documentSet}/documents/{path} — one document of the report
 * on show (`shown.documentSetId`), e.g. path "summary/one-month".
 */
export const getDocument = (reportId, documentSetId, path) =>
  v2Get(
    `${reportPath(reportId)}/document-sets/${id(documentSetId)}/documents/${String(path)
      .split('/')
      .map(id)
      .join('/')}`
  );

const V2_BASE_PATH = '/api/v2/competitive-intel';

/**
 * A page of a long list (handoff §8.2), e.g. the timeframe's comments. `itemsUrl` is the list's
 * `links.items` from `shown.lists` (a full URL — never built by hand); the call keeps its path under
 * the V2 base. `query`: { 'filter[topic]', 'filter[product]', 'filter[platform]', 'filter[verdict]',
 * 'filter[emotion]', q, sort, page, perPage }; empty values are left out. Resolves `{ data }` with
 * { total, items: [{ id, item, links }], nextCursor, facets }.
 */
export const getListItems = (itemsUrl, query = {}) => {
  const url = String(itemsUrl || '');
  const at = url.indexOf(V2_BASE_PATH);
  const path = at >= 0 ? url.slice(at + V2_BASE_PATH.length) : url;
  const params = new URLSearchParams(
    Object.entries(query).filter(([, value]) => value != null && value !== '')
  ).toString();
  const joiner = path.includes('?') ? '&' : '?';
  return v2Get(`${path}${params ? `${joiner}${params}` : ''}`);
};
